import React, { useEffect, useState } from "react";
import { ProviderTrainingCompliance, ProviderTrainingComplianceEntry } from "../../types/training";
import { Search, ChevronRight, ArrowUp, ArrowDown, CheckCircle2, Bell, Loader2, MessageSquare } from "lucide-react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { sendProviderNotificationForTrainingForMultiple, sendTeamsReminderForProviderTraining } from "../../apicalls/trainingService";
import moment from "moment";

interface Props {
  complianceData: ProviderTrainingCompliance[];
}

const PIE_COLORS = ["#34D399", "#EF4444"]; // Green: compliant, Red: non-compliant

type View = 'table' | 'chart';

interface TableProps {
  complianceData: ProviderTrainingCompliance[];
  onSelect: (provider: ProviderTrainingCompliance) => void;
}

function ProviderTrainingComplianceTable({ complianceData, onSelect }: TableProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [serviceFilter, setServiceFilter] = useState<string>("all");
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [sortConfig, setSortConfig] = useState<{
    key: 'providerName' | 'totalComplianceCount' | 'totalNotComplianceCount' | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'asc'
  });

  const [filteredData, setFilteredData] = useState(complianceData.filter(item => {
    if (serviceFilter !== "all") {
      switch (serviceFilter) {
        case "Direct Support":
          if (!item.directSupportServices) return false;
          break;
        case "Remote Support":
          if (!item.remoteSupportServices) return false;
          break;
      }
    }
    const term = searchTerm.toLowerCase();
    return (
      item.providerName.toLowerCase().includes(term) ||
      item.providerID.toLowerCase().includes(term)
    );
  }));

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(new Set(filteredData.map(item => item.providerID)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (providerID: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(providerID);
    } else {
      newSelected.delete(providerID);
    }
    setSelectedItems(newSelected);
  };

  const isAllSelected = filteredData.length > 0 && filteredData.every(item => selectedItems.has(item.providerID));
  const isIndeterminate = selectedItems.size > 0 && selectedItems.size < filteredData.length;

  const handleSort = (key: 'providerName' | 'totalComplianceCount' | 'totalNotComplianceCount') => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedData = [...filteredData].sort((a, b) => {
      let aValue: string | number = a[key];
      let bValue: string | number = b[key];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        if (direction === 'asc') {
          return aValue.localeCompare(bValue);
        }
        return bValue.localeCompare(aValue);
      } else {
        if (direction === 'asc') {
          return (aValue as number) - (bValue as number);
        }
        return (bValue as number) - (aValue as number);
      }
    });
    setFilteredData(sortedData);
  };

  const getSortIcon = (key: 'providerName' | 'totalComplianceCount' | 'totalNotComplianceCount') => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? 
      <ArrowUp className="tw-w-4 tw-h-4 tw-inline-block tw-ml-1" /> : 
      <ArrowDown className="tw-w-4 tw-h-4 tw-inline-block tw-ml-1" />;
  };

  useEffect(() => {
    let filtered = complianceData;
    
    switch (serviceFilter) {
      case "Direct Support":
        filtered = complianceData.filter(item => item.directSupportServices);
        break;
      case "Remote Support":
        filtered = complianceData.filter(item => item.remoteSupportServices);
        break;
    }

    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(item => 
      item.providerName.toLowerCase().includes(term) ||
      item.providerID.toLowerCase().includes(term)
    );

    setFilteredData(filtered);
  }, [serviceFilter, searchTerm, complianceData]);

  return (
    <div className="tw-overflow-x-auto">
      <div className="tw-flex tw-items-center tw-gap-4 tw-mb-4">
        <div>
          <select
            value={serviceFilter}
            onChange={e => setServiceFilter(e.target.value)}
            className="tw-form-select tw-border-gray-300 tw-rounded tw-px-2 tw-py-1 tw-text-sm"
          >
            <option value="all">All Services</option>
            <option value="Direct Support">Direct Support</option>
            <option value="Remote Support">Remote Support</option>
          </select>
        </div>
        <div className="tw-relative tw-flex-1">
          <span className="tw-absolute tw-left-2 tw-top-1.5 tw-text-gray-400">
            <Search className="tw-w-4 tw-h-4" />
          </span>
          <input
            type="text"
            placeholder="Search providers"
            className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <table className="tw-w-full tw-text-sm tw-text-left">
        <thead>
          <tr className="tw-bg-gray-50">
            <th 
              className="tw-px-4 tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100"
              onClick={() => handleSort('providerName')}
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                Provider Name
                {getSortIcon('providerName')}
              </div>
            </th>
            <th className="tw-px-4 tw-py-2">Services</th>
            <th 
              className="tw-px-4 tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100"
              onClick={() => handleSort('totalComplianceCount')}
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                Compliant Count
                {getSortIcon('totalComplianceCount')}
              </div>
            </th>
            <th 
              className="tw-px-4 tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100"
              onClick={() => handleSort('totalNotComplianceCount')}
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                Non-Compliant Count
                {getSortIcon('totalNotComplianceCount')}
              </div>
            </th>
            <th className="tw-px-4 tw-py-2 text-center">Compliance Ratio</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map(item => {
            const pieData = [
              { name: "Compliant", value: item.totalComplianceCount },
              { name: "Non-Compliant", value: item.totalNotComplianceCount },
            ];
            return (
              <tr
                key={item.providerID}
                className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50"
              >
                <td 
                  className="tw-px-4 tw-py-2 tw-cursor-pointer"
                  onClick={() => onSelect(item)}
                >
                  <div className="tw-flex tw-flex-col">
                    <span className="tw-font-medium tw-text-gray-900">{item.providerName}</span>
                    <span className="tw-text-xs tw-text-gray-500">Email: {item.providerEmail}</span>
                  </div>
                </td>
                <td className="tw-px-4 tw-py-2">
                  <div className="tw-flex tw-flex-wrap tw-gap-1">
                    {item.directSupportServices && (
                      <span className="tw-bg-blue-100 tw-text-blue-700 tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium">
                        Direct Support
                      </span>
                    )}
                    {item.remoteSupportServices && (
                      <span className="tw-bg-purple-100 tw-text-purple-700 tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium">
                        Remote Support
                      </span>
                    )}
                  </div>
                </td>
                <td className="tw-px-4 tw-py-2">{item.totalComplianceCount}</td>
                <td className="tw-px-4 tw-py-2">{item.totalNotComplianceCount}</td>
                <td className="tw-px-4 tw-py-2 tw-flex tw-justify-center">
                  <ResponsiveContainer width={40} height={40}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={16}
                        innerRadius={8}
                        paddingAngle={2}
                      >
                        {pieData.map((_, idx) => (
                          <Cell key={`cell-${idx}`} fill={PIE_COLORS[idx]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ProviderTrainingComplianceChart({ complianceData }: Props) {
  const chartData = complianceData
    .map((item) => {
      const total = item.totalComplianceCount + item.totalNotComplianceCount;
      const compliantRate = total > 0 ? (item.totalComplianceCount / total) * 100 : 0;
      const nonCompliantRate = total > 0 ? (item.totalNotComplianceCount / total) * 100 : 0;
      return {
        providerName: item.providerName,
        compliantRate: parseFloat(compliantRate.toFixed(1)),
        nonCompliantRate: parseFloat(nonCompliantRate.toFixed(1)),
      };
    })
    .sort((a, b) => b.nonCompliantRate - a.nonCompliantRate);

  const chartHeight = Math.max(200, chartData.length * 40);

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart
        layout="vertical"
        data={chartData}
        margin={{ top: 20, right: 30, left: 150, bottom: 20 }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis type="number" domain={[0, 100]} tickFormatter={val => `${val}%`} />
        <YAxis type="category" dataKey="providerName" width={150} tick={{ fontSize: 12 }} />
        <Tooltip
          formatter={(value: number, name: string) => [
            `${value}%`, name === 'compliantRate' ? 'Compliant' : 'Non-Compliant'
          ]}
          wrapperStyle={{ fontSize: 12 }}
        />
        <Bar dataKey="compliantRate" name="Compliant" barSize={10} fill="#34D399" />
        <Bar dataKey="nonCompliantRate" name="Non-Compliant" barSize={10} fill="#EF4444" />
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function ProviderTrainingComplianceDashboard({ complianceData }: Props) {
  const [view, setView] = useState<View>('chart');
  const [selectedProvider, setSelectedProvider] = useState<ProviderTrainingCompliance | null>(null);

  return (
    <div className="tw-bg-white tw-rounded-2xl tw-shadow tw-p-6 tw-w-full">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <h2 className="tw-text-lg tw-font-semibold">
          {selectedProvider ? (
            <div className="tw-flex tw-items-center tw-space-x-2">
              <button 
                onClick={() => setSelectedProvider(null)}
                className="tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors"
              >
                All Providers
              </button>
              <ChevronRight className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span>{selectedProvider.providerName}</span>
            </div>
          ) : (
            'Provider Training Compliance Overview'
          )}
        </h2>
        <div className="tw-flex tw-space-x-2">
          {selectedProvider ? (
            <></>
          ) : (
            <>
              <button
                onClick={() => setView('chart')}
                className={`tw-px-4 tw-py-2 tw-rounded ${
                  view === 'chart' ? 'tw-bg-cyan-600 tw-text-white' : 'tw-bg-gray-200'
                }`}
              >
                Chart
              </button>
              <button
                onClick={() => setView('table')}
                className={`tw-px-4 tw-py-2 tw-rounded ${
                  view === 'table' ? 'tw-bg-cyan-600 tw-text-white' : 'tw-bg-gray-200'
                }`}
              >
                Table
              </button>
            </>
          )}
        </div>
      </div>
      {selectedProvider ? (
        <ProviderTrainingEntryTable complianceEntries={selectedProvider.complianceEntries} providerEmail={selectedProvider.providerEmail} providerName={selectedProvider.providerName} />
      ) : view === 'table' ? (
        <ProviderTrainingComplianceTable
          complianceData={complianceData}
          onSelect={setSelectedProvider}
        />
      ) : (
        <ProviderTrainingComplianceChart complianceData={complianceData} />
      )}
    </div>
  );
}

// Component to display individual provider's training entries
function ProviderTrainingEntryTable({ complianceEntries, providerEmail, providerName }: { complianceEntries: ProviderTrainingComplianceEntry[], providerEmail: string, providerName: string }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [sortConfig, setSortConfig] = useState<{
    key: 'trainingName' | 'client' | 'status' | 'due' | null;
    direction: 'asc' | 'desc';
  }>({
    key: null,
    direction: 'asc'
  });

  const [filteredData, setFilteredData] = useState(complianceEntries);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(new Set(filteredData.map((_, index) => index)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (index: number, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(index);
    } else {
      newSelected.delete(index);
    }
    setSelectedItems(newSelected);
  };

  const isAllSelected = filteredData.length > 0 && filteredData.every((_, index) => selectedItems.has(index));
  const isIndeterminate = selectedItems.size > 0 && selectedItems.size < filteredData.length;

  // Notification states
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationSent, setNotificationSent] = useState(false);
  const [teamsReminderLoading, setTeamsReminderLoading] = useState(false);
  const [teamsReminderSent, setTeamsReminderSent] = useState(false);

  const handleSendNotifications = async () => {
    if (selectedItems.size === 0) return;
    
    setNotificationLoading(true);
    setNotificationSent(false);
    
    try {
      // Get selected compliance entries
      const selectedComplianceEntries = filteredData.filter((_, index) => selectedItems.has(index));
      
      // Send notifications
      await sendProviderNotificationForTrainingForMultiple(selectedComplianceEntries, providerEmail, providerName, (error) => {
        console.error('Notification error:', error);
      });
      
      setNotificationSent(true);
      setTimeout(() => setNotificationSent(false), 1500);
    } catch (error) {
      console.error('Failed to send notifications:', error);
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleSendTeamsReminder = async () => {
    if (selectedItems.size === 0) return;
    
    setTeamsReminderLoading(true);
    setTeamsReminderSent(false);
    
    try {
      // Get selected compliance entries
      const selectedComplianceEntries = filteredData.filter((_, index) => selectedItems.has(index));
      
      // Send Teams reminders (we'll need to get the provider email from context or props)
      // For now, we'll use a placeholder email - you may need to adjust this based on your data structure
      await sendTeamsReminderForProviderTraining(selectedComplianceEntries, providerEmail);
      
      setTeamsReminderSent(true);
      setTimeout(() => setTeamsReminderSent(false), 1500);
    } catch (error) {
      console.error('Failed to send Teams reminders:', error);
    } finally {
      setTeamsReminderLoading(false);
    }
  };

  const handleSort = (key: 'trainingName' | 'client' | 'status' | 'due') => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedData = [...filteredData].sort((a, b) => {
      const aValue = a[key] || '';
      const bValue = b[key] || '';
      if (direction === 'asc') {
        return aValue.localeCompare(bValue);
      }
      return bValue.localeCompare(aValue);
    });
    setFilteredData(sortedData);
  };

  const getSortIcon = (key: 'trainingName' | 'client' | 'status' | 'due') => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? 
      <ArrowUp className="tw-w-4 tw-h-4 tw-inline-block tw-ml-1" /> : 
      <ArrowDown className="tw-w-4 tw-h-4 tw-inline-block tw-ml-1" />;
  };

  useEffect(() => {
    const filtered = complianceEntries.filter(item => {
      const term = searchTerm.toLowerCase();
      return (
        item.trainingName.toLowerCase().includes(term) ||
        item.client.toLowerCase().includes(term) ||
        item.code.toLowerCase().includes(term)
      );
    });
    setFilteredData(filtered);
  }, [searchTerm, complianceEntries]);

  return (
    <div className="tw-overflow-x-auto">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <div className="tw-relative tw-flex-1">
          <span className="tw-absolute tw-left-2 tw-top-1.5 tw-text-gray-400">
            <Search className="tw-w-4 tw-h-4" />
          </span>
          <input
            type="text"
            placeholder="Search trainings"
            className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="tw-flex tw-gap-2 tw-ml-4">
          <button
            className="tw-flex tw-items-center tw-gap-2 tw-bg-cyan-50 tw-text-cyan-700 tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-border-cyan-200 tw-text-sm tw-font-medium hover:tw-bg-cyan-100 tw-relative tw-min-w-[140px]"
            onClick={handleSendNotifications}
            disabled={notificationLoading || selectedItems.size === 0}
          >
            {notificationLoading ? (
              <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
            ) : notificationSent ? (
              <CheckCircle2 className="tw-w-4 tw-h-4 tw-text-green-500 tw-animate-fade-in-out" />
            ) : (
              <Bell className="tw-w-4 tw-h-4" />
            )}
            <span>{notificationSent ? "Sent!" : "Send Notifications"}</span>
          </button>

          <button
            className="tw-flex tw-items-center tw-gap-2 tw-bg-purple-50 tw-text-purple-700 tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-border-purple-200 tw-text-sm tw-font-medium hover:tw-bg-purple-100 tw-relative tw-min-w-[140px]"
            onClick={handleSendTeamsReminder}
            disabled={teamsReminderLoading || selectedItems.size === 0}
          >
            {teamsReminderLoading ? (
              <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
            ) : teamsReminderSent ? (
              <CheckCircle2 className="tw-w-4 tw-h-4 tw-text-green-500 tw-animate-fade-in-out" />
            ) : (
              <MessageSquare className="tw-w-4 tw-h-4" />
            )}
            <span>{teamsReminderSent ? "Sent!" : "Send Teams Reminder"}</span>
          </button>
        </div>
      </div>
      <table className="tw-w-full tw-text-sm tw-text-left">
        <thead>
          <tr className="tw-bg-gray-50">
            <th className="tw-px-4 tw-py-2 tw-w-12">
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={(input) => {
                  if (input) input.indeterminate = isIndeterminate;
                }}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="tw-w-4 tw-h-4 tw-text-cyan-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-cyan-500 focus:tw-ring-2"
              />
            </th>
            <th 
              className="tw-px-4 tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100"
              onClick={() => handleSort('trainingName')}
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                Training Name
                {getSortIcon('trainingName')}
              </div>
            </th>
            <th 
              className="tw-px-4 tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100"
              onClick={() => handleSort('client')}
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                Client
                {getSortIcon('client')}
              </div>
            </th>
            <th className="tw-px-4 tw-py-2">Code</th>
            <th 
              className="tw-px-4 tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100"
              onClick={() => handleSort('status')}
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                Status
                {getSortIcon('status')}
              </div>
            </th>
            <th className="tw-px-4 tw-py-2">Score</th>
            <th 
              className="tw-px-4 tw-py-2 tw-cursor-pointer hover:tw-bg-gray-100"
              onClick={() => handleSort('due')}
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                Due Date
                {getSortIcon('due')}
              </div>
            </th>
            <th className="tw-px-4 tw-py-2">Last Reminded</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((item, index) => (
            <tr key={index} className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50">
              <td className="tw-px-4 tw-py-2">
                <input
                  type="checkbox"
                  checked={selectedItems.has(index)}
                  onChange={(e) => handleSelectItem(index, e.target.checked)}
                  className="tw-w-4 tw-h-4 tw-text-cyan-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-cyan-500 focus:tw-ring-2"
                />
              </td>
              <td className="tw-px-4 tw-py-2">
                <div className="tw-flex tw-flex-col">
                  <span className="tw-font-medium tw-text-gray-900">{item.trainingName}</span>
                  {item.badge && (
                    <span className="tw-bg-cyan-100 tw-text-cyan-700 tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-semibold tw-self-start tw-mt-1">
                      {item.badge}
                    </span>
                  )}
                </div>
              </td>
              <td className="tw-px-4 tw-py-2">{item.client || "—"}</td>
              <td className="tw-px-4 tw-py-2">{item.code}</td>
              <td className="tw-px-4 tw-py-2">
                <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-semibold ${
                  item.status === 'Not Certified' ? 'tw-bg-red-100 tw-text-red-700' :
                  item.status === 'Certified' ? 'tw-bg-green-100 tw-text-green-700' :
                  'tw-bg-yellow-100 tw-text-yellow-700'
                }`}>
                  {item.status}
                </span>
              </td>
              <td className="tw-px-4 tw-py-2">{item.score}</td>
              <td className="tw-px-4 tw-py-2">{item.due}</td>
              <td className="tw-px-4 tw-py-2">{item.lastReminded ? moment(item.lastReminded).format("YYYY-MM-DD") : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
